import React, { useContext, useEffect, useState } from "react";
import ApplicationAPI from "../../../api/commercial/application";
import { CardholderLocalState } from "../../../pages/cardholder-basic-information/types";
import AuthenticatedApplicationFlow from "./application-flow";
import DashboardFlow from "./dashboard-flow";
import { LoadingContext } from "../../../context/loaderContext";
import { ProductContext } from "../../../context/product-context/productContext";
import ExistingApplicationFoundModal from "../../../pages/shared/components/existing-application-found";
import {
  BasicAccountApplicationDocument,
  UnfinishedApplicationsDocument,
} from "../../../api/commercial/generated-types";
import { ProductRefType } from "../../../context/product-context/types";
import { Product } from "../../../context/product-context/product";

interface UnfinishedApplicationsContextType {
  unfinishedApplicationsDocument: UnfinishedApplicationsDocument;
}

export const UnfinishedApplicationsContext = React.createContext(
  {} as UnfinishedApplicationsContextType,
);

export default function AuthenticatedSection(): React.JSX.Element {
  const [document, setDocument] =
    useState<UnfinishedApplicationsDocument>(undefined);

  const [hasDisabledProduct, setHasDisabledProduct] = useState(false);
  const [productFromServer, setProductFromServer] = useState<ProductRefType>();
  const { setOverlayActive, overlayActive } = useContext(LoadingContext);

  const [additionalCAFlowLocalState, setAdditionalCAFlowLocalState] =
    useState<CardholderLocalState>(null);

  const { setSelectedProduct, findPersistedProductByRef, selectedProduct } =
    useContext(ProductContext);

  const [notifyExistingApplication, setNotifyExistingApplication] =
    useState<boolean>(false);

  const syncModal = (product: Product) => {
    if (
      !!selectedProduct &&
      product?.productRef !== selectedProduct?.productRef
    ) {
      setNotifyExistingApplication(true);
    }
  };

  const extractProductRefFromDocument = (
    document: UnfinishedApplicationsDocument,
  ): ProductRefType | undefined => {
    if (
      document.basicAccountApplication !== null &&
      document.basicAccountApplication.productRef
    ) {
      return document.basicAccountApplication.productRef as ProductRefType;
    }

    return document?.cardApplication?.productRef as ProductRefType;
  };

  const reloadState = () => {
    // data has been fetched from backend, local state needs to invalidated
    setAdditionalCAFlowLocalState(null);

    setOverlayActive(true);

    ApplicationAPI.getUnfinishedApplications().then((response) => {
      const newDocument = response as UnfinishedApplicationsDocument;

      // get the BAA first, if not available get the CA.
      const productRefFromServer = extractProductRefFromDocument(newDocument);
      setProductFromServer(productRefFromServer);

      setDocument(newDocument);

      if (productRefFromServer) {
        const selectedProductFromBackend = findPersistedProductByRef(
          productRefFromServer as ProductRefType,
        );
        syncModal(selectedProductFromBackend);
        setSelectedProduct(selectedProductFromBackend);

        if (selectedProductFromBackend?.isActive) {
          setHasDisabledProduct(false);
        } else {
          setHasDisabledProduct(true);
        }
      }

      setOverlayActive(false);
    });
  };

  useEffect(() => {
    reloadState();
  }, []);

  const areApplicationsNotFinished = () => {
    if (additionalCAFlowLocalState) {
      return true;
    }
    if (
      document?.basicAccountApplication ||
      document?.cardApplication ||
      document?.noValidApplications
    ) {
      return true;
    }
    return false;
  };

  const resolveSection = () => {
    // document can be null when all applications are finished. That is a valid value.
    if (overlayActive || document === undefined) {
      return null;
    }

    if (areApplicationsNotFinished()) {
      return (
        <UnfinishedApplicationsContext.Provider
          value={{ unfinishedApplicationsDocument: document }}
        >
          <ExistingApplicationFoundModal
            isModalOpen={notifyExistingApplication}
            setNotifyExistingApplication={setNotifyExistingApplication}
          />
          <AuthenticatedApplicationFlow
            additionalCAFlowLocalState={additionalCAFlowLocalState}
            basicAccountApplication={document.basicAccountApplication}
            cardApplication={document.cardApplication}
            noValidApplications={document.noValidApplications}
            signingProcess={document.signingProcess}
            triggerReload={() => reloadState()}
            hasDisabledProduct={hasDisabledProduct}
            productFromServer={productFromServer}
          />
        </UnfinishedApplicationsContext.Provider>
      );
      // application(s) are all finished.
    } else {
      return (
        <DashboardFlow
          reloadState={reloadState}
          addAdditionalCardApplication={(
            basicAccountApplicationRef: string,
            productRef: string,
            companyName: string,
            isSoleProprietorship: boolean,
            hasDecentralizedInvoicing: BasicAccountApplicationDocument["hasDecentralizedInvoicing"],
            limit: number,
          ) => {
            setAdditionalCAFlowLocalState({
              basicAccountApplicationRef,
              productRef,
              companyName,
              isSoleProprietorship,
              hasDecentralizedInvoicing,
              limit,
            });
          }}
        />
      );
    }
  };

  return resolveSection();
}
